import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContactsIcon } from "@components/icons";
import { Modal } from "@components/Modal/Modal";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import {
  prefetchKycForm,
  useKycCurrentFormQuery,
} from "@services/knowYourCustomer/queries";
import { KycFormStep } from "@services/knowYourCustomer/types";
import { IS_APP_KYC_MODE } from "@/constants";
import { CloseModal } from "./components/CloseModal/CloseModal";
import { ConfirmationForm } from "./components/ConfirmationForm/ConfirmationForm";
import { ContactInformationForm } from "./components/ContactInformationForm/ContactInformationForm";
import { EarningExpenseForm } from "./components/EarningExpenseForm/EarningExpenseForm";
import { IncomeInformationForm } from "./components/IncomeInformationForm/IncomeInformationForm";
import { KnowYourCustomerAuthentication } from "./components/KnowYourCustomerAuthentication/KnowYourCustomerAuthentication";
import { PersonalInformationForm } from "./components/PersonalInformationForm/PersonalInformationForm";
import { PoliticalEffectedForm } from "./components/PoliticalEffectedForm/PoliticalEffectedForm";
import { PoliticalEffectedPeopleForm } from "./components/PoliticalEffectedPeopleForm/PoliticalEffectedPeopleForm";
import { SuccessModal } from "./components/SuccessModal/SuccessModal";

interface IKnowYourCustomerModalProps extends IUseModalProps {
  onPause?: () => void;
}

export const KnowYourCustomerModal: React.FC<IKnowYourCustomerModalProps> = (
  props,
) => {
  const { onPause, ...rest } = props;
  const navigate = useNavigate();
  const modal = useModal({ ...rest, important: true });

  const { data: knowYourCustomerCurrentForm, refetch: refetchCurrentForm } =
    useKycCurrentFormQuery();

  const [step, setStep] = useState<KycFormStep>(KycFormStep.INITIALIZING);

  const changeStep = async (step: KycFormStep) => {
    await prefetchKycForm(step);
    setStep(step);
  };

  useEffect(() => {
    if (knowYourCustomerCurrentForm) {
      setStep(knowYourCustomerCurrentForm.currentForm);
    }
  }, [knowYourCustomerCurrentForm]);

  useEffect(() => {
    if (
      IS_APP_KYC_MODE &&
      (step === KycFormStep.SUCCESS || step === KycFormStep.COMPLETED)
    ) {
      navigate("/status/success");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <Modal
      modal={{
        ...modal,
        onCustomClose: () =>
          step === KycFormStep.SUCCESS
            ? modal.close
            : setStep(
                [
                  KycFormStep.IDENTIFICATION_FORM,
                  KycFormStep.SIGN_DOCUMENT_FORM,
                ].includes(step)
                  ? KycFormStep.CLOSE_FORM_AUTHENTICATION
                  : KycFormStep.CLOSE_FORM,
              ),
        hideCloseButton: step.toString().startsWith("closeForm"),
      }}
      className="md:max-w-xl"
      badgeProps={{ icon: ContactsIcon }}
    >
      {step.toString().startsWith("closeForm") && (
        <CloseModal
          variant={
            step.toString().includes("authentication")
              ? "authentication"
              : "default"
          }
          onClose={() => {
            modal.close();
            onPause?.();
          }}
          onContinue={() =>
            refetchCurrentForm().then((response) =>
              setStep(response.data?.currentForm ?? KycFormStep.INITIALIZING),
            )
          }
        />
      )}
      {step === KycFormStep.PERSONAL_INFORMATION_FORM && (
        <PersonalInformationForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.CONTACT_INFORMATION_FORM && (
        <ContactInformationForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.INCOME_INFORMATION_FORM && (
        <IncomeInformationForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.EARNING_EXPENSE_FORM && (
        <EarningExpenseForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.POLITICAL_AFFECTED_FORM && (
        <PoliticalEffectedForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.POLITICAL_AFFECTED_PEOPLE_FORM && (
        <PoliticalEffectedPeopleForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.CONFIRMATION_FORM && (
        <ConfirmationForm onChangeStep={changeStep} />
      )}
      {step === KycFormStep.IDENTIFICATION_FORM && (
        <KnowYourCustomerAuthentication
          authPurpose="identification"
          onSuccess={() => changeStep(KycFormStep.SIGN_DOCUMENT_FORM)}
          onChangeStep={changeStep}
        />
      )}
      {step === KycFormStep.SIGN_DOCUMENT_FORM && (
        <KnowYourCustomerAuthentication
          authPurpose="signDocument"
          onSuccess={() => changeStep(KycFormStep.SUCCESS)}
          onChangeStep={changeStep}
        />
      )}
      {step === KycFormStep.SUCCESS && <SuccessModal onClose={modal.close} />}
    </Modal>
  );
};
